import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Character, Rarity, Variant } from "../types";

const RARITY_STYLE: Record<Rarity, { badge: string; border: string; glow: string; accent: string }> = {
  common: {
    badge: "bg-muted text-muted-foreground",
    border: "border-muted",
    glow: "",
    accent: "text-muted-foreground",
  },
  uncommon: {
    badge: "bg-emerald-600 text-white hover:bg-emerald-600",
    border: "border-emerald-800",
    glow: "shadow-sm shadow-emerald-500/20",
    accent: "text-emerald-400",
  },
  rare: {
    badge: "bg-blue-600 text-white hover:bg-blue-600",
    border: "border-blue-800",
    glow: "shadow-md shadow-blue-500/20",
    accent: "text-blue-400",
  },
  epic: {
    badge: "bg-purple-600 text-white hover:bg-purple-600",
    border: "border-purple-800",
    glow: "shadow-lg shadow-purple-500/30",
    accent: "text-purple-400",
  },
  legendary: {
    badge: "bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-500 hover:to-amber-500",
    border: "border-yellow-700",
    glow: "shadow-lg shadow-yellow-500/30",
    accent: "text-yellow-400",
  },
};

const VARIANT_CLASS: Record<Variant, string> = {
  normal: "",
  shiny: "variant-shiny",
  rainbow: "variant-rainbow",
  glitch: "variant-glitch",
};

const VARIANT_BADGE_CLASS: Record<Variant, string> = {
  normal: "",
  shiny: "bg-slate-300 text-slate-900",
  rainbow: "bg-gradient-to-r from-red-400 via-green-400 to-blue-400 text-white",
  glitch: "bg-green-500 text-black",
};

function generateStats(name: string, rarity: Rarity) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  hash = Math.abs(hash);

  const base: Record<Rarity, number> = {
    common: 18,
    uncommon: 38,
    rare: 58,
    epic: 75,
    legendary: 90,
  };

  const b = base[rarity];
  const stat = (seed: number) =>
    Math.min(99, Math.max(1, b + ((hash * seed) % 20) - 10));

  return {
    rizz: stat(7),
    gpa: Number(Math.min(4.0, Math.max(0.5, b / 25 + (hash % 15) / 10)).toFixed(1)),
    clout: stat(13),
    mog: stat(31),
  };
}

interface CharacterCardProps {
  character: Character;
  variant?: Variant;
  revealed?: boolean;
  onReveal?: () => void;
  compact?: boolean;
}

export function CharacterCard({
  character,
  variant = "normal",
  revealed = true,
  onReveal,
  compact = false,
}: CharacterCardProps) {
  const style = RARITY_STYLE[character.rarity];
  const stats = generateStats(character.name, character.rarity);
  const initial = character.name.replace("The ", "").charAt(0);
  const variantClass = VARIANT_CLASS[variant];
  const isInteractive = !revealed && Boolean(onReveal);

  return (
    <div
      className={`card-container ${compact ? "card-sm" : "card-md"} ${isInteractive ? "is-interactive" : ""}`}
      onClick={isInteractive ? onReveal : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onReveal?.();
              }
            }
          : undefined
      }
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `Reveal ${character.name} card` : undefined}
    >
      <div className={`card-inner ${revealed ? "flipped" : ""}`}>
        {/* Card Back */}
        <div className="card-face card-back">
          <Card className="h-full flex flex-col items-center justify-center gap-1 py-0 select-none border-muted">
            <div className="text-3xl font-bold tracking-tighter text-muted-foreground/50">
              MOG
            </div>
            <p className="text-[10px] text-muted-foreground/40 mt-1">
              tap to reveal
            </p>
          </Card>
        </div>

        {/* Card Front */}
        <div className="card-face card-front">
          <Card className={`h-full flex flex-col overflow-hidden gap-0 py-0 ${style.border} ${style.glow} ${variantClass}`}>
            <CardHeader className="p-2 pb-0 flex flex-row items-start justify-between gap-1 space-y-0">
              <div className="flex items-center gap-1 flex-wrap min-w-0">
                <Badge className={`text-[9px] ${style.badge}`}>
                  {character.rarity}
                </Badge>
                {variant !== "normal" && (
                  <Badge className={`text-[9px] ${VARIANT_BADGE_CLASS[variant]}`}>
                    {variant}
                  </Badge>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">
                #{String(character.id).padStart(3, "0")}
              </span>
            </CardHeader>

            <CardContent className={`flex items-center justify-center p-2 ${compact ? "flex-none" : "flex-1"}`}>
              <div className={`w-full aspect-square rounded-md bg-muted/50 flex items-center justify-center border border-border overflow-hidden ${compact ? "max-h-[92px] sm:max-h-[100px]" : "max-h-[110px]"}`}>
                {character.image_url ? (
                  <img
                    src={`/api/img?url=${encodeURIComponent(character.image_url)}`}
                    alt={character.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span className={`text-4xl font-black ${style.accent} opacity-70`}>
                    {initial}
                  </span>
                )}
              </div>
            </CardContent>

            <div className={`px-2 text-center ${compact ? "pb-2" : "pb-1"}`}>
              <h3
                className={`font-semibold leading-tight break-words line-clamp-2 ${compact ? "text-xs min-h-[2rem]" : "text-sm min-h-[2.3rem]"}`}
              >
                {character.name}
              </h3>
              {character.nickname && (
                <p
                  className={`${style.accent} italic leading-tight line-clamp-1 ${compact ? "text-[9px]" : "text-[10px]"}`}
                >
                  &ldquo;{character.nickname}&rdquo;
                </p>
              )}
            </div>

            {!compact && (
              <div className="grid grid-cols-4 gap-0.5 px-2 py-1.5 mt-auto">
                {Object.entries(stats).map(([key, val]) => (
                  <div key={key} className="text-center">
                    <div className="text-[8px] text-muted-foreground uppercase font-medium">
                      {key}
                    </div>
                    <div className={`text-xs font-semibold ${style.accent}`}>
                      {val}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!compact && character.description && (
              <CardFooter className="p-2 pt-0">
                <p className="text-[8px] text-muted-foreground italic text-center leading-tight line-clamp-2 w-full">
                  {character.description}
                </p>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
